import { GreenCircleCheckmark } from '../../svg/GreenCircleCheckmark';
import { Route, Switch, useLocation, useParams } from 'wouter';
import { AuthedPage } from '../../app';
import { OrganizationChallengeOne } from './ChallengeOne';
import { OrganizationChallengeTwo } from './ChallengeTwo';
import { OrganizationChallengeThree } from './ChallengeThree';
import { ApplicationResultContent } from '../../application/components/ApplicationResultContent';
import { Button } from '@platform/components';
import { ChallengeFormPageProps } from './consts';
import ErrorPage from 'apps/web/src/ErrorPage';
import { BusinessChallenges } from '@platform/client';

const ChallengesFormSuccess = () => {
  return (
    <ApplicationResultContent
      description={
        <div className='flex flex-col items-center gap-4 pt-10'>
          <GreenCircleCheckmark size={80} />
          <p className='text-lg text-center mt-4'>
            Your challenges have been submitted. We will follow up with your hand-picked
            recommendations within 10 days.
          </p>
          <Button
            variant='primary'
            size='md'
            additionalClassnames='w-fit'
            href={`platform/dashboard`}
          >
            Back to dashboard
          </Button>
        </div>
      }
    ></ApplicationResultContent>
  );
};

type ChallengeFormStep = {
  component: React.ComponentType<ChallengeFormPageProps>;
  url: string;
};

const ChallengeFormSteps: Record<string, ChallengeFormStep> = {
  one: {
    component: OrganizationChallengeOne,
    url: '/',
  },
  two: {
    component: OrganizationChallengeTwo,
    url: '/two',
  },
  three: {
    component: OrganizationChallengeThree,
    url: '/three',
  },
  success: {
    component: ChallengesFormSuccess,
    url: '/success',
  },
};

export const BusinessChallengesForm = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const [location, navigate] = useLocation();
  const goToNextStep = () => {
    const steps = Object.keys(ChallengeFormSteps);
    const currentIndex = steps.findIndex((step) => {
      return ChallengeFormSteps[step].url === location;
    });
    const nextStep =
      currentIndex < steps.length - 1 ? steps[currentIndex + 1] : steps[currentIndex];
    const nextUrl = ChallengeFormSteps[nextStep].url;
    navigate(nextUrl);
  };
  const goToPreviousStep = () => {
    const steps = Object.keys(ChallengeFormSteps);
    const currentIndex = steps.findIndex((step) => ChallengeFormSteps[step].url === location);
    const previousStep = currentIndex > 0 ? steps[currentIndex - 1] : steps[currentIndex];
    const previousUrl =
      currentIndex > 0 ? ChallengeFormSteps[previousStep].url : '~/platform/dashboard';
    navigate(previousUrl);
  };
  const { data: challengeData, isSuccess } =
    BusinessChallenges.useGetOrganizationBusinessChallenges(parseInt(organizationId));
  return (
    isSuccess && (
      <AuthedPage>
        <Switch>
          <Route path='/error'>
            <ErrorPage />
          </Route>
          {Object.entries(ChallengeFormSteps).map(([_key, step]) => {
            const { component: Component, url } = step;
            return (
              <Route key={url} path={url}>
                <Component
                  onBack={goToPreviousStep}
                  onContinue={goToNextStep}
                  organizationId={parseInt(organizationId)}
                  challengeData={challengeData}
                />
              </Route>
            );
          })}
        </Switch>
      </AuthedPage>
    )
  );
};

export default BusinessChallengesForm;
